/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

export function isUndefined(value: unknown) {
  return typeof value === 'undefined';
}

export function isEmpty(value: unknown) {
  return !value || value === '';
}

export function isNull(value: unknown) {
  return value === null;
}

export function isValidValue(value: unknown) {
  return !(isUndefined(value) || isEmpty(value) || isNull(value));
}

export function isFunction(value: unknown) {
  return typeof value === 'function';
}

export function isNumber(value: unknown) {
  return typeof value === 'number';
}

export function isString(value: unknown) {
  return typeof value === 'string';
}

export function isBoolean(value: unknown) {
  return typeof value === 'boolean';
}

export function isObject(value: unknown) {
  return typeof value === 'object';
}

export function isNumberFinite(value: unknown) {
  return isNumber(value) && isFinite(value as number);
}

export function isVowel(letter: string): boolean {
  return ['a', 'e', 'i', 'o', 'u'].indexOf(letter) >= 0;
}

export function ucFirst(text: string) {
  const [part, ...split] = text.split(/\s/g);

  const ucd = part
    .toLowerCase()
    .split(/(?=['|-])/g)
    .map((word: any) =>
      word.indexOf('-') + word.indexOf("'") > -2
        ? word.slice(0, 2).toUpperCase() + word.slice(2)
        : word.slice(0, 1).toUpperCase() + word.slice(1)
    )
    .join('');

  return [ucd, ...split].join(' ');
}

export function applyPrecision(num: number, precision: number) {
  if (precision <= 0) {
    return Math.round(num);
  }

  const tho = 10 ** precision;

  return Math.round(num * tho) / tho;
}

export function extractDeepPropertyByMapKey(obj: any, map: string): any {
  const keys = map.split('.');

  return keys.reduce((prop: any, key: string) => {
    return !isUndefined(prop) && !isNull(prop) && !isUndefined(prop[key]) ? prop[key] : undefined;
  }, obj);
}

export function extractDeepPropertyByParentMapKey(obj: any, map: string): any {
  const keys = map.split('.');
  const tail = keys.pop();
  const props = extractDeepPropertyByMapKey(obj, keys.join('.'));

  return { props, tail };
}

export function getKeysTwoObjects(obj: any, other: any): string[] {
  return [...Object.keys(obj), ...Object.keys(other)].filter((key, index, array) => array.indexOf(key) === index);
}

export function isDeepEqual(obj: any, other: any): boolean {
  if (obj === other) {
    return true;
  }

  if (!isObject(obj) || !isObject(other)) {
    return false;
  }

  return getKeysTwoObjects(obj, other).every((key: any): boolean => {
    if (obj[key] === other[key]) {
      return true;
    }

    if (isObject(obj[key]) && isObject(other[key])) {
      return isDeepEqual(obj[key], other[key]);
    }

    return false;
  });
}

export function rotate(arr: any[], n = 0): any[] {
  if (!arr) return arr;

  const copy = arr.slice(0);
  n = n % copy.length;
  if (copy.length && n < 0) n += copy.length;
  copy.push(...copy.splice(0, n));
  return copy;
}

export function assert(condition: boolean, message?: string): void {
  condition || console.error('ASSERT FAILED - :' + message);
}

export function containsAll(a: any[], b: any[]) {
  if (a.length && b.length) {
    return b.every(element => {
      return a.includes(element);
    });
  }
  return false;
}

export function isContainsAny(input: any[], master: any[]): boolean {
  let lookupMap = new Map<any, boolean>();
  if (master !== null) {
    for (let i = 0, len = master.length; i < len; i++) {
      lookupMap.set(master[i], true);
    }
  }

  let result = false;
  for (let i = 0, len = input.length; i < len; i++) {
    if (lookupMap.get(input[i])) {
      result = true;
      break;
    }
  }
  lookupMap = null;
  return result;
}

export function isSubsetOf(input: any[], master: any[]): boolean {
  if (input.length > master.length) return false;
  let lookupMap = new Map<any, boolean>();
  for (let i = 0, len = master.length; i < len; i++) {
    lookupMap.set(master[i], true);
  }

  let result = true;
  for (let i = 0, len = input.length; i < len; i++) {
    if (!lookupMap.get(input[i])) {
      result = false;
      break;
    }
  }
  lookupMap = null;
  return result;
}

export function deleteUndefinedNullValues(obj: any) {
  Object.keys(obj).forEach(function (key) {
    if (isUndefined(obj[key]) || isNull(obj[key])) {
      delete obj[key];
    }
  });
}

export function getRangeValues(startValue, endValue) {
  if (startValue?.isAfter(endValue)) {
    const val = startValue;
    startValue = endValue;
    endValue = val;
  }
  return {
    startValue: startValue,
    endValue: endValue
  };
}
